import { PaymentsDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  BankingBankAccountFragment,
  CreatePaymentDocument,
  PaymentsDataCollectionDocument,
  PaymentsPageDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { i18nDateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

export interface PaymentFormValues {
  label: string
  description?: string
  bankAccountId: string
  transferIds?: number[]
}

export interface NewPaymentFormProps {
  form: ReturnType<typeof useForm<PaymentFormValues>>
  bankAccounts: BankingBankAccountFragment[]
}

export const useCreatePaymentForm = ({
  onSuccess,
  transferIds = null,
}: {
  onSuccess: (id: number) => void
  transferIds?: number[] | null
}) => {
  const today = moment().format('YYYY-MM-DD')
  const weekStart = moment().startOf('week').format('YYYY-MM-DD')
  const weekEnd = moment().endOf('week').format('YYYY-MM-DD')
  const { company, employee } = useCurrentSession()
  const [createBankingPayment, { loading: loadingCreation }] = useMutation(CreatePaymentDocument, {
    extractorFn: (data) => data.bankingMutations.createBankingPayment,
    refetchQueries: [
      PaymentsDataTableDocument,
      PaymentsDataCollectionDocument,
      {
        query: PaymentsPageDocument, // We need updated values in transfer insights
        variables: {
          companyId: company.id,
          featureNames: ['dev_finance_swan_reactivation'],
          todayDate: today,
          weekRange: { from: weekStart, to: weekEnd },
        },
      },
    ],
    awaitRefetchQueries: true,
  })

  const defaultLabel = i18n.t('banking.bulk_payment.forms.default_name', {
    date: i18nDateToString(new Date(), 'ui_abbreviated_month_year'),
  })

  const creationForm = useForm<PaymentFormValues>({
    defaultValues: {
      label: defaultLabel,
      bankAccountId: '',
      description: '',
      transferIds: transferIds || [],
    },
    validateOnChange: true,
    validate: (values) => {
      const errors: Record<string, string> = {}
      if (!values.label) {
        errors.label = i18n.t('banking.bulk_payment.forms.validation_mandatory')
      } else if (values.label.trim().length <= 0) {
        errors.label = i18n.t('banking.bulk_payment.forms.validation_min_length', { length: 1 })
      } else if (values.label.length > 64) {
        errors.label = i18n.t('banking.bulk_payment.forms.validation_max_length', { length: 64 })
      }

      if (!values.bankAccountId) {
        errors.bankAccountId = i18n.t('banking.bulk_payment.forms.validation_mandatory')
      }
      return errors
    },
    onSubmit: async (values) => {
      const result = await createBankingPayment({
        variables: {
          authorId: employee.id,
          label: values.label,
          description: values.description,
          bankAccountId: Number(values.bankAccountId),
          transferIds: transferIds,
        },
      })
      const handled = handleResult(result, (errors) => {
        creationForm.setErrors(errors)
      })
      if (handled.success) {
        const updatedPayment = handled.data.payment
        app.setFlash({
          type: 'correct',
          message: i18n.t('banking.bulk_payment.new_bulk_payment_modal.payment_created'),
        })
        updatedPayment && onSuccess(updatedPayment.id)
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_error'),
        })
      }
      return handled
    },
  })

  return {
    form: creationForm,
    loading: loadingCreation,
  }
}
